import type { Event, EventButtonType } from "../../models/events";
import type { PointRule } from "../../models/pointRule";
import { formatPointRewardCost } from "../../models/pointReward";
import { getApiErrorCode, isObjectRecord } from "./api-errors";

/**
 * Valor inicial de `active` en EventForm.
 * Creación: true por defecto (negocio). Edición: respeta el valor persistido (incluye false).
 */
export function resolveEventFormActive(
  entity?: Pick<Event, "active"> | null
): boolean {
  if (entity == null) {
    return true;
  }
  return entity.active;
}

/**
 * Lugares ya tomados en el evento (inscriptos + sus invitados), derivados de lo
 * que expone el backend. `null` cuando todavía no hay datos del evento —creación,
 * o el detalle sin cargar— y por lo tanto no hay nada que validar.
 */
export function resolveOccupiedSpots(
  entity?: Pick<Event, "amount" | "available"> | null
): number | null {
  if (entity == null || entity.available == null) {
    return null;
  }
  return Math.max((entity.amount ?? 0) - entity.available, 0);
}

export interface ParticipatingHostSpotInput {
  /** Cupos totales según el formulario: puede diferir del valor guardado. */
  totalSpots: number;
  /** Resultado de `resolveOccupiedSpots`. */
  occupiedSpots: number | null;
  /**
   * El host ya tiene una inscripción propia. En ese caso activar el toggle no
   * consume un cupo nuevo, así que se permite aunque el evento esté completo.
   */
  hostAlreadyRegistered: boolean;
}

/**
 * GTK-717 / TC-PAY-15. El host que participa se inscribe y ocupa un cupo real. Si
 * el evento está completo, activar "Host participa en el evento" dejaría más
 * inscriptos confirmados que cupos; Producto decidió no desplazar a ningún
 * asistente ya confirmado, así que el toggle no se activa y se avisa.
 *
 * Se mide contra los cupos del formulario, no contra los guardados: ampliar el
 * cupo y activar el host en la misma edición es válido.
 */
export function hasSpotForParticipatingHost({
  totalSpots,
  occupiedSpots,
  hostAlreadyRegistered,
}: ParticipatingHostSpotInput): boolean {
  if (occupiedSpots === null || hostAlreadyRegistered) {
    return true;
  }
  return totalSpots - occupiedSpots >= 1;
}

/**
 * GTK-780-cap. El venue puede tener un `max_capacity` (nullable en DB). Cuando
 * lo tiene, se compara contra los cupos totales del formulario (`amount`),
 * que ya contempla los invitados por persona del lado del negocio —no se
 * vuelve a multiplicar por `guests` acá—.
 *
 * Es solo una advertencia visual, en las secciones de Fecha y cupos y de
 * Ubicación: a diferencia del cupo del host (GTK-717 / TC-PAY-15), no bloquea
 * el submit. Un venue puede ceder ante un cupo mayor negociado fuera del
 * sistema, y no siempre está el dato cargado (`maxCapacity` null), en cuyo
 * caso no hay nada que advertir.
 */
export function exceedsVenueCapacity(
  maxCapacity: number | null | undefined,
  totalAttendees: number
): boolean {
  if (maxCapacity == null) {
    return false;
  }
  return totalAttendees > maxCapacity;
}

/** Configuración del CTA tal como la maneja el form: strings vacíos en vez de null. */
export interface EventFormCtaValues {
  buttonType: EventButtonType;
  buttonLabel: string;
  buttonUrl: string;
  showAttendeesInfo: boolean;
  buttonSendSession: boolean;
  buttonOpenInApp: boolean;
}

/** Lo que se envía al backend: etiqueta y URL en null fuera de CUSTOM_LINK. */
export interface EventCtaPayload {
  buttonType: EventButtonType;
  buttonLabel: string | null;
  buttonUrl: string | null;
  showAttendeesInfo: boolean;
  buttonSendSession: boolean;
  buttonOpenInApp: boolean;
}

type EventCtaEntity = Pick<
  Event,
  | "buttonType"
  | "buttonLabel"
  | "buttonUrl"
  | "showAttendeesInfo"
  | "buttonSendSession"
  | "buttonOpenInApp"
>;

/**
 * GTK-768 — Valores iniciales del CTA en EventForm. Creación: inscripción
 * estándar con los asistentes visibles, igual que los eventos ya existentes.
 * Edición: lo persistido, salvo que la modalidad no sea la estándar — ahí los
 * cupos y asistentes nunca pueden estar visibles, así que el toggle arranca en
 * false aunque el backend haya quedado desalineado.
 */
export function resolveEventFormCtaValues(
  entity?: Partial<EventCtaEntity> | null
): EventFormCtaValues {
  const buttonType = (entity?.buttonType || "INSCRIPTION") as EventButtonType;

  const isCustomLink = buttonType === "CUSTOM_LINK";

  return {
    buttonType,
    buttonLabel: entity?.buttonLabel || "",
    buttonUrl: entity?.buttonUrl || "",
    showAttendeesInfo:
      buttonType !== "INSCRIPTION" ? false : entity?.showAttendeesInfo ?? true,
    // Los dos controles del botón describen cómo se abre el link, así que sin
    // botón personalizado no hay nada que configurar.
    buttonSendSession: isCustomLink ? entity?.buttonSendSession ?? false : false,
    buttonOpenInApp: isCustomLink ? entity?.buttonOpenInApp ?? false : false,
  };
}

/**
 * GTK-768 — Normaliza el CTA antes de enviarlo. El backend aplica las mismas dos
 * reglas (limpiar etiqueta/URL fuera de CUSTOM_LINK y ocultar los asistentes
 * fuera de INSCRIPTION); acá se aplican también para que lo que se ve en el form
 * sea lo que se guarda.
 */
export function buildEventCtaPayload(values: EventFormCtaValues): EventCtaPayload {
  const isCustomLink = values.buttonType === "CUSTOM_LINK";

  return {
    buttonType: values.buttonType,
    buttonLabel: isCustomLink ? values.buttonLabel?.trim() || null : null,
    buttonUrl: isCustomLink ? values.buttonUrl?.trim() || null : null,
    showAttendeesInfo:
      values.buttonType === "INSCRIPTION" ? values.showAttendeesInfo : false,
    buttonSendSession: isCustomLink ? values.buttonSendSession : false,
    buttonOpenInApp: isCustomLink ? values.buttonOpenInApp : false,
  };
}

/**
 * GTK-887 — La regla `event_bonus` activa del tenant, o null. Sin ella la sección de
 * puntos extra no se muestra: el backend no acredita el bonus de un evento sin la regla.
 */
export function findActiveEventBonusRule(
  rules?: PointRule[] | null
): PointRule | null {
  return rules?.find((rule) => rule.actionType === "event_bonus" && rule.active) ?? null;
}

/**
 * GTK-869 / PTS-018 — El tope de puntos extra del tenant es el `points_value` de la regla
 * event_bonus. null = sin tope (el backend todavía no deja cargarlo en esa regla).
 */
export function resolveEventBonusMaxPoints(rule: PointRule | null): number | null {
  const maxPoints = rule?.pointsValue;
  return maxPoints != null && maxPoints > 0 ? maxPoints : null;
}

export interface EventFormBonusValues {
  bonusEnabled: boolean;
  bonusPoints: number | null;
}

/** Creación: apagado. Edición: encendido solo si el evento ya tiene un bonus guardado. */
export function resolveEventFormBonusValues(
  entity?: Pick<Event, "bonusPoints"> | null
): EventFormBonusValues {
  const bonusPoints = entity?.bonusPoints != null && entity.bonusPoints > 0 ? entity.bonusPoints : null;
  return { bonusEnabled: bonusPoints !== null, bonusPoints };
}

/**
 * Lo que viaja de `bonusPoints`. Si la sección no se muestra (flag apagado o regla
 * inactiva) el campo no viaja: en la edición el backend saltea lo que no llega, así que
 * el bonus guardado queda como estaba en vez de borrarse. Con la sección visible, el
 * toggle apagado manda null, que es "el evento no da puntos extra".
 */
export function buildEventBonusPayload(
  values: EventFormBonusValues,
  sectionVisible: boolean
): { bonusPoints?: number | null } {
  if (!sectionVisible) return {};
  return { bonusPoints: values.bonusEnabled ? values.bonusPoints : null };
}

/**
 * Puntos extra que conserva un evento duplicado. El diálogo de duplicar no tiene la sección de
 * puntos, así que la copia se queda sin bonus si el tenant ya no lo permite o si supera el tope:
 * si no, el backend la rechaza y no hay dónde sacarlo.
 */
export function resolveDuplicatedEventBonusPoints(
  bonusPoints: number | null | undefined,
  pointsEnabled: boolean,
  rules?: PointRule[] | null
): number | null {
  if (bonusPoints == null || !pointsEnabled) return null;
  const bonusRule = findActiveEventBonusRule(rules);
  if (bonusRule === null) return null;
  const maxPoints = resolveEventBonusMaxPoints(bonusRule);
  return maxPoints !== null && bonusPoints > maxPoints ? null : bonusPoints;
}

/** La copia se crea igual, pero sin el bonus que `resolveDuplicatedEventBonusPoints` descartó. */
export const DUPLICATED_EVENT_BONUS_DROPPED_NOTICE =
  "El evento se duplicó sin los puntos extra: el tenant ya no los permite o superan su tope. Podés cargarlos editando la copia.";

/** Texto del badge de la card en la app: "+15 puntos". */
export function formatEventBonusBadge(bonusPoints: number): string {
  return `+${formatPointRewardCost(bonusPoints)}`;
}

/** E12 — Cambiar el bonus no recalcula los check-ins que ya pasaron. */
export const EVENT_BONUS_CHECKINS_NOTICE = "Los check-ins ya realizados no se modifican.";

/** El payload cambia el bonus guardado. Sin `bonusPoints` (sección oculta) no lo toca. */
export function changesEventBonus(
  entity: Pick<Event, "bonusPoints"> | null | undefined,
  payload: { bonusPoints?: number | null }
): boolean {
  if (payload.bonusPoints === undefined) return false;
  return (payload.bonusPoints ?? null) !== (entity?.bonusPoints ?? null);
}

/**
 * GTK-869 — La edición responde `has_checkins: true` cuando el evento ya tiene check-ins.
 * Se acepta en la raíz o dentro de `data`, y en snake o camelCase.
 */
export function eventUpdateHasCheckins(response: unknown): boolean {
  if (!isObjectRecord(response)) return false;
  const body = isObjectRecord(response.data) ? response.data : response;
  return body.has_checkins === true || body.hasCheckins === true;
}

/** Mismo máximo de sanidad que el backend: un cero de más repartiría miles de puntos por asistente. */
export const MAX_EVENT_BONUS_POINTS = 10000;

export const EVENT_BONUS_NOT_ENABLED_CODE = "BONUS_NOT_ENABLED";

export const EVENT_BONUS_NOT_ENABLED_MESSAGE =
  "Los puntos extra por evento no están habilitados para este tenant. Desactivá “Este evento otorga puntos extra” o activá la regla Bonus por evento en Puntos → Reglas.";

/** PTS-018 — El backend rechaza el bonus cuando el flag o la regla event_bonus están apagados. */
export function isEventBonusNotEnabledError(error: unknown): boolean {
  return getApiErrorCode(error) === EVENT_BONUS_NOT_ENABLED_CODE;
}
